import { Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Api } from 'src/app/services/api';
import { Globals } from 'src/app/services/globals';
import {
    ARTWORK_GENERATION_MODELS,
    ArtworkGenerationModel,
    ArtworkGenerationQuality,
    ArtworkOrientation,
    DEFAULT_CANVAS_CATEGORY,
    DEFAULT_CANVAS_MOCKUP_PROMPT,
    DEFAULT_MOCKUP_PROMPT,
    DEFAULT_POSTER_CATEGORY,
    DEFAULT_SHOPIFY_VENDOR,
    IArtworkGenerationBatch,
    IArtworkGenerationCategory,
    IArtworkGenerationShopifySettings,
    IArtworkToGenerate
} from '../../../../../../../common/common-interfaces/artwork-generation-batch';
import { IArtworkGenerationSettings } from '../../../../../../../common/common-interfaces/global-settings';
import { canvasSizesByName, posterSizesByName } from '../../../../../../../common/common-types/poster-size';
import { getBorderModificationCode } from '../../../../../../../common/common-helpers/print-file';

/** Batch statuses that still change on their own, so the list needs polling. */
const LIVE_STATUSES = ['pending', 'processing', 'creating_posters'];

const POLL_INTERVAL_MS = 15000;

const STYLE_PROMPT_PLACEHOLDER = 'Clearly painterly fine art, not photography: visible oil/gouache brushwork, matte pigments, subtle canvas tooth, simplified shapes, confident lost edges, human irregularity, sophisticated color relationships. Avoid photorealism.';

const MOTIF_NAME_PLACEHOLDER = 'Morning Orchard';

const MOTIF_PROMPT_PLACEHOLDER = 'sunlit apple orchard, narrow path and long grass';

/** The sizes the poster page's Shopify tab pre-selects, reused here. */
const DEFAULT_SHOPIFY_POSTER_SIZES: { [size: string]: boolean } = { '21x30': true, '30x40': true, '50x70': true, '70x100': true };
const DEFAULT_SHOPIFY_CANVAS_SIZES: { [size: string]: boolean } = { '30x40': true, '50x70': true, '70x100': true };

/** A Shopify product taxonomy category. */
interface IShopifyCategory {
    id: string;
    name: string;
    fullName: string;
    isLeaf: boolean;
}

/** Key for the new-batch form's own category search state. */
const NEW_BATCH_KEY = 'new';

/** The two category pickers: one per product type. */
type CategoryKind = 'poster' | 'canvas';
const CATEGORY_KINDS: CategoryKind[] = ['poster', 'canvas'];

/** Search state for one instance of the category picker. */
interface ICategoryState {
    search: string;
    results: IShopifyCategory[];
    loading: boolean;
}

/** Shopify size/price/tag editing state for one batch card. */
interface IShopifyEditor {
    posterSizes: { [size: string]: boolean };
    canvasSizes: { [size: string]: boolean };
    posterPrices: { [size: string]: number };
    canvasPrices: { [size: string]: number };
    tags: string;
    posterCategory: IArtworkGenerationCategory | null;
    canvasCategory: IArtworkGenerationCategory | null;
    vendor: string;
    createPassepartoutVariants: boolean;
    passepartoutPrices: { [size: string]: number };
    createMockup: boolean;
    mockupPrompt: string;
    useCanvasMockupPrompt: boolean;
    canvasMockupPrompt: string;
    publishProduct: boolean;
    saving: boolean;
}

/** One row in the motif editor. */
interface IMotifRow {
    name: string;
    motifPrompt: string;
}

/**
 * Splits CSV text into rows of cells. Handles quoted cells (with embedded
 * delimiters, line breaks and doubled quotes) and guesses the delimiter from
 * the header line, since Excel with a Swedish locale saves with semicolons.
 */
function parseCsv(text: string): string[][] {
    text = text.replace(/^\uFEFF/, '');

    let headerLine = text.split(/\r?\n/, 1)[0];
    let delimiter = [',', ';', '\t']
        .map(d => ({ d, count: headerLine.split(d).length }))
        .sort((a, b) => b.count - a.count)[0].d;

    let rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;

    for (let i = 0; i < text.length; i++) {
        let c = text[i];

        if (quoted) {
            if (c === '"' && text[i + 1] === '"') {
                cell += '"';
                i++;
            } else if (c === '"') {
                quoted = false;
            } else {
                cell += c;
            }
        } else if (c === '"') {
            quoted = true;
        } else if (c === delimiter) {
            row.push(cell);
            cell = '';
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && text[i + 1] === '\n')
                i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        } else {
            cell += c;
        }
    }

    if (cell.length || row.length) {
        row.push(cell);
        rows.push(row);
    }

    return rows;
}

@Component({
    selector: 'generate-artworks',
    templateUrl: './generate-artworks.component.html',
    styleUrls: ['./generate-artworks.component.scss'],
    standalone: false
})
export class GenerateArtworksComponent implements OnInit, OnDestroy {
    batches: IArtworkGenerationBatch[] = [];
    settings: IArtworkGenerationSettings;

    loading = true;
    saving = false;
    error = '';

    stylePromptPlaceholder = STYLE_PROMPT_PLACEHOLDER;
    motifNamePlaceholder = MOTIF_NAME_PLACEHOLDER;
    motifPromptPlaceholder = MOTIF_PROMPT_PLACEHOLDER;

    /** Batch id -> expanded, so the thumbnail grid survives a poll refresh. */
    expanded: { [batchId: string]: boolean } = {};
    /** Artwork identifier -> whether its full prompt is shown. */
    promptShown: { [identifier: string]: boolean } = {};
    /** Artwork identifier -> prompt being edited, for rejected artworks. */
    promptDrafts: { [identifier: string]: string } = {};
    savingPrompt: { [identifier: string]: boolean } = {};
    /** "<artwork identifier>_<type>" -> a detach in flight, to disable its button. */
    detaching: { [key: string]: boolean } = {};
    /** The artwork shown full size in the lightbox, if any. */
    previewedArtwork: IArtworkToGenerate = null;
    /** Batch id -> Shopify options being edited on that batch's card. */
    shopifyEditors: { [batchId: string]: IShopifyEditor } = {};

    /** Category search state, keyed by NEW_BATCH_KEY or a batch id. */
    categoryStates: { [key: string]: ICategoryState } = {};
    /** Configured passepartout width per poster size, from global settings. */
    passepartoutWidths: { [size: string]: number } = {};
    /** Guards the carry-forward so a poll refresh cannot overwrite typed input. */
    private autofilled = false;

    availablePosterSizes = Object.keys(posterSizesByName);
    availableCanvasSizes = Object.keys(canvasSizesByName);

    models = Object.keys(ARTWORK_GENERATION_MODELS) as ArtworkGenerationModel[];

    newBatch: {
        name: string,
        stylePrompt: string,
        motifs: IMotifRow[],
        orientation: ArtworkOrientation,
        model: ArtworkGenerationModel,
        quality: ArtworkGenerationQuality,
        createPosters: boolean,
        createCanvas: boolean,
        createShopifyProduct: boolean
    } = this.emptyBatch();

    @ViewChild('csvImportDialog', { static: true }) csvImportDialog: TemplateRef<any>;
    private csvImportDialogRef: MatDialogRef<any> | null = null;
    csvImportError = '';
    csvDragOver = false;

    private pollHandle: any;

    constructor(private api: Api, public globals: Globals, private dialog: MatDialog) {

    }

    async ngOnInit() {
        this.seedShopifyEditor(null);
        await this.load();
        this.pollHandle = setInterval(() => this.pollIfLive(), POLL_INTERVAL_MS);
    }

    ngOnDestroy() {
        if (this.pollHandle)
            clearInterval(this.pollHandle);
    }

    private emptyBatch() {
        return {
            name: '',
            stylePrompt: '',
            // Starts with one blank row so there is always something to type in.
            motifs: [this.emptyMotif()],
            orientation: 'portrait' as ArtworkOrientation,
            model: 'gpt-image-1-mini' as ArtworkGenerationModel,
            quality: ARTWORK_GENERATION_MODELS['gpt-image-2'].defaultQuality,
            createPosters: false,
            createCanvas: false,
            createShopifyProduct: false
        };
    }

    /**
     * The Shopify settings the new-batch form and every batch card share.
     *
     * Both places render the same tabs from one template, so they must hold the
     * same shape - keeping two parallel structures is what previously let
     * options be added to one place and quietly missed on the other.
     */
    private emptyShopifyEditor(shopify?: IArtworkGenerationShopifySettings): IShopifyEditor {
        let toMap = (sizes: string[], fallback: { [size: string]: boolean }) => {
            if (!sizes || !sizes.length)
                return Object.assign({}, fallback);

            let map: { [size: string]: boolean } = {};
            sizes.forEach(size => map[size] = true);
            return map;
        };

        return {
            posterSizes: toMap(shopify ? shopify.posterSizes : null, DEFAULT_SHOPIFY_POSTER_SIZES),
            canvasSizes: toMap(shopify ? shopify.canvasSizes : null, DEFAULT_SHOPIFY_CANVAS_SIZES),
            posterPrices: Object.assign({}, shopify ? shopify.posterPrices : {}),
            canvasPrices: Object.assign({}, shopify ? shopify.canvasPrices : {}),
            tags: shopify && shopify.tags ? shopify.tags.join(', ') : '',
            // A batch saved before the categories were split kept one choice for
            // both, so it stands in for either until the user changes it.
            posterCategory: (shopify && (shopify.posterCategory || shopify.category)) || DEFAULT_POSTER_CATEGORY,
            canvasCategory: (shopify && (shopify.canvasCategory || shopify.category)) || DEFAULT_CANVAS_CATEGORY,
            vendor: (shopify && shopify.vendor) || DEFAULT_SHOPIFY_VENDOR,
            createPassepartoutVariants: !!(shopify && shopify.createPassepartoutVariants),
            passepartoutPrices: Object.assign({}, shopify ? shopify.passepartoutPrices : {}),
            createMockup: !!(shopify && shopify.createMockup),
            mockupPrompt: (shopify && shopify.mockupPrompt) || DEFAULT_MOCKUP_PROMPT,
            useCanvasMockupPrompt: !!(shopify && shopify.useCanvasMockupPrompt),
            canvasMockupPrompt: (shopify && shopify.canvasMockupPrompt) || DEFAULT_CANVAS_MOCKUP_PROMPT,
            publishProduct: !!(shopify && shopify.publishProduct),
            saving: false
        };
    }

    private emptyMotif(): IMotifRow {
        return { name: '', motifPrompt: '' };
    }

    /**
     * Clears the form after a batch is created. The Shopify editor has to be
     * dropped as well - it lives beside the batch cards' editors, so leaving it
     * would carry the last submission's options into the next batch unasked.
     */
    private resetNewBatch() {
        this.newBatch = this.emptyBatch();
        delete this.shopifyEditors[NEW_BATCH_KEY];
        this.seedShopifyEditor(null);
        this.autofilled = false;
    }

    async load() {
        try {
            let result = await this.api.store().get<{ batches: IArtworkGenerationBatch[], settings: IArtworkGenerationSettings, passepartoutWidths: { [size: string]: number } }>('/artwork-generations');
            this.batches = result.batches;
            this.settings = result.settings;
            this.passepartoutWidths = result.passepartoutWidths || {};
            this.error = '';

            // Seeded here rather than lazily from the template, so polling never
            // discards options the user is part way through editing.
            for (let batch of this.batches)
                this.seedShopifyEditor(batch);

            this.autofillFromLastBatch();
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte hämta batcharna';
        }
        finally {
            this.loading = false;
        }
    }

    /**
     * Carries the Shopify prices, category and tags forward from the most recent
     * batch that published to Shopify, so they do not have to be retyped.
     *
     * Only applied while the form is still untouched - once the user has entered
     * anything, a poll refresh must not overwrite it.
     */
    private autofillFromLastBatch() {
        if (this.autofilled)
            return;

        let previous = this.batches.find(b => b.shopify && b.shopify.createProduct);

        if (!previous)
            return;

        this.autofilled = true;

        // Rebuilt from the previous batch wholesale, so every option carries
        // forward - including the passepartout prices and the mockup scene,
        // which a field-by-field copy kept forgetting.
        this.shopifyEditors[NEW_BATCH_KEY] = this.emptyShopifyEditor(previous.shopify);
    }

    /**
     * The category picker appears both on the new-batch form and on every batch
     * card, so its search state is keyed per place. `null` means the new-batch
     * form; anything else is a batch.
     */
    private categoryKey(batch: IArtworkGenerationBatch | null) {
        return batch ? String(batch._id) : NEW_BATCH_KEY;
    }

    /** Poster and canvas each have their own picker, so each needs its own state. */
    private categoryStateKey(batch: IArtworkGenerationBatch | null, kind: CategoryKind) {
        return `${this.categoryKey(batch)}_${kind}`;
    }

    private seedCategoryState(batch: IArtworkGenerationBatch | null) {
        for (let kind of CATEGORY_KINDS) {
            let key = this.categoryStateKey(batch, kind);

            if (!this.categoryStates[key])
                this.categoryStates[key] = { search: '', results: [], loading: false };
        }
    }

    categoryState(batch: IArtworkGenerationBatch | null, kind: CategoryKind): ICategoryState {
        return this.categoryStates[this.categoryStateKey(batch, kind)];
    }

    /** The category currently chosen in that place, if any. */
    selectedCategory(batch: IArtworkGenerationBatch | null, kind: CategoryKind) {
        let editor = this.shopifyEditor(batch);

        if (!editor)
            return null;

        return kind == 'canvas' ? editor.canvasCategory : editor.posterCategory;
    }

    /** Puts Shopify's own suggestion back after the choice has been changed. */
    resetCategory(batch: IArtworkGenerationBatch | null, kind: CategoryKind) {
        let editor = this.shopifyEditor(batch);

        if (!editor)
            return;

        if (kind == 'canvas')
            editor.canvasCategory = DEFAULT_CANVAS_CATEGORY;
        else
            editor.posterCategory = DEFAULT_POSTER_CATEGORY;
    }

    /** Searches Shopify's product taxonomy. An empty term returns the top level. */
    async searchCategories(batch: IArtworkGenerationBatch | null, kind: CategoryKind) {
        if (!this.isShopifyStore)
            return;

        let state = this.categoryState(batch, kind);

        if (!state)
            return;

        state.loading = true;

        try {
            let query = state.search.trim();
            let result = await this.api.store().get<{ categories: IShopifyCategory[] }>(
                `/artwork-generations/shopify-categories${query ? `?q=${encodeURIComponent(query)}` : ''}`);
            state.results = result.categories || [];
        }
        catch (e) {
            console.log(e);
            state.results = [];
        }
        finally {
            state.loading = false;
        }
    }

    selectCategory(batch: IArtworkGenerationBatch | null, kind: CategoryKind, category: IShopifyCategory) {
        let chosen = { id: category.id, name: category.fullName || category.name };

        let editor = this.shopifyEditor(batch);

        if (editor) {
            if (kind == 'canvas')
                editor.canvasCategory = chosen;
            else
                editor.posterCategory = chosen;
        }

        // Collapse the result list once something has been picked.
        let state = this.categoryState(batch, kind);
        if (state)
            state.results = [];
    }

    clearCategory(batch: IArtworkGenerationBatch | null, kind: CategoryKind) {
        let editor = this.shopifyEditor(batch);

        if (!editor)
            return;

        if (kind == 'canvas')
            editor.canvasCategory = null;
        else
            editor.posterCategory = null;
    }

    /** Only refetches while something is actually moving. */
    private async pollIfLive() {
        if (this.batches.some(b => LIVE_STATUSES.indexOf(b.status) >= 0))
            await this.load();
    }


    // -------------------------------------------------------------------------
    // Creating batches
    // -------------------------------------------------------------------------

    addMotif() {
        this.newBatch.motifs.push(this.emptyMotif());
    }

    removeMotif(index: number) {
        this.newBatch.motifs.splice(index, 1);

        // Never leave the editor with no rows at all.
        if (!this.newBatch.motifs.length)
            this.addMotif();
    }

    openCsvImport() {
        this.csvImportError = '';
        this.csvDragOver = false;
        this.csvImportDialogRef = this.dialog.open(this.csvImportDialog, {
            maxWidth: '560px',
            width: 'calc(100vw - 32px)',
            autoFocus: false
        });
    }

    onCsvDragOver(event: DragEvent) {
        event.preventDefault();
        this.csvDragOver = true;
    }

    onCsvDragLeave(event: DragEvent) {
        event.preventDefault();
        this.csvDragOver = false;
    }

    onCsvDrop(event: DragEvent) {
        event.preventDefault();
        this.csvDragOver = false;

        let file = event.dataTransfer && event.dataTransfer.files[0];
        if (file)
            this.importCsvFile(file);
    }

    onCsvFileSelected(event: Event) {
        let input = event.target as HTMLInputElement;
        let file = input.files && input.files[0];
        // Cleared so picking the same file again still fires a change.
        input.value = '';

        if (file)
            this.importCsvFile(file);
    }

    private async importCsvFile(file: File) {
        this.csvImportError = '';

        let text: string;
        try {
            text = await file.text();
        } catch (e) {
            this.csvImportError = 'Kunde inte läsa filen';
            return;
        }

        let rows = parseCsv(text).filter(r => r.some(cell => cell.trim().length > 0));
        if (!rows.length) {
            this.csvImportError = 'Filen är tom';
            return;
        }

        let header = rows[0].map(h => h.trim().toLowerCase());
        let nameIndex = header.indexOf('name');
        let descriptionIndex = header.indexOf('description');
        if (descriptionIndex < 0) {
            this.csvImportError = 'Filen saknar kolumnen "Description"';
            return;
        }

        let motifs: IMotifRow[] = rows.slice(1)
            .map(r => ({
                name: nameIndex >= 0 ? (r[nameIndex] || '').trim() : '',
                motifPrompt: (r[descriptionIndex] || '').trim()
            }))
            .filter(m => m.motifPrompt.length > 0);

        if (!motifs.length) {
            this.csvImportError = 'Filen innehåller inga rader med beskrivning';
            return;
        }

        // Imported rows are appended, taking the place of any blank rows.
        let existing = this.newBatch.motifs.filter(m => m.name.trim() || m.motifPrompt.trim());
        this.newBatch.motifs = existing.concat(motifs);

        if (this.csvImportDialogRef)
            this.csvImportDialogRef.close();
    }

    /** Rows with an actual description - blank rows are ignored on submit. */
    private filledMotifs() {
        return this.newBatch.motifs.filter(m => m.motifPrompt.trim().length > 0);
    }

    get motifCount() {
        return this.filledMotifs().length;
    }

    get canCreate() {
        return !this.saving && this.newBatch.stylePrompt.trim().length > 0 && this.motifCount > 0;
    }

    /** True when this store can publish to Shopify at all. */
    get isShopifyStore() {
        return !!(this.globals.storeSettings
            && this.globals.storeSettings.platform == 'shopify'
            && this.globals.storeSettings.shopifySettings);
    }

    /**
     * Shopify publishing only makes sense once the batch creates something to
     * publish, so the option is hidden until a product type is picked.
     */
    get canOfferShopify() {
        return this.canOfferShopifyFor(null);
    }

    /** Preview of the SKU code a size will produce, e.g. 35 -> B35W. */
    passepartoutCode(size: string) {
        return getBorderModificationCode(this.passepartoutWidths[size]);
    }

    private selectedSizes(selection: { [size: string]: boolean }) {
        return Object.keys(selection).filter(size => selection[size]);
    }

    private newBatchPayload() {
        return {
            name: this.newBatch.name,
            stylePrompt: this.newBatch.stylePrompt,
            motifs: this.filledMotifs().map(m => ({
                name: m.name.trim(),
                motifPrompt: m.motifPrompt.trim()
            })),
            orientation: this.newBatch.orientation,
            model: this.newBatch.model,
            quality: this.newBatch.quality,
            createPosters: this.newBatch.createPosters,
            createCanvas: this.newBatch.createCanvas,
            shopify: this.shopifyPayload(null, this.canOfferShopify && this.newBatch.createShopifyProduct)
        };
    }

    async saveDraft() {
        if (!this.canCreate)
            return;

        this.saving = true;

        try {
            await this.api.store().post<IArtworkGenerationBatch>('/artwork-generations', this.newBatchPayload());
            this.resetNewBatch();
            await this.load();
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte spara utkastet';
        }
        finally {
            this.saving = false;
        }
    }

    async createAndSubmit() {
        if (!this.canCreate)
            return;

        this.saving = true;

        try {
            let batch = await this.api.store().post<IArtworkGenerationBatch>('/artwork-generations', this.newBatchPayload());
            await this.api.store().post(`/artwork-generations/${batch._id}/submit`, {});
            this.resetNewBatch();
            await this.load();
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte skicka batchen';
        }
        finally {
            this.saving = false;
        }
    }


    // -------------------------------------------------------------------------
    // Acting on existing batches
    // -------------------------------------------------------------------------

    private replaceBatch(updated: IArtworkGenerationBatch) {
        let index = this.batches.findIndex(b => String(b._id) == String(updated._id));
        if (index >= 0)
            this.batches[index] = updated;
    }

    async submit(batch: IArtworkGenerationBatch) {
        try {
            this.replaceBatch(await this.api.store().post<IArtworkGenerationBatch>(`/artwork-generations/${batch._id}/submit`, {}));
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte skicka batchen';
        }
    }

    async approve(batch: IArtworkGenerationBatch, artwork: IArtworkToGenerate) {
        try {
            this.replaceBatch(await this.api.store().post<IArtworkGenerationBatch>(`/artwork-generations/${batch._id}/artworks/${artwork.identifier}/approve`, {}));
        }
        catch (e) {
            console.log(e);
        }
    }

    async reject(batch: IArtworkGenerationBatch, artwork: IArtworkToGenerate) {
        try {
            this.replaceBatch(await this.api.store().post<IArtworkGenerationBatch>(`/artwork-generations/${batch._id}/artworks/${artwork.identifier}/reject`, {}));
        }
        catch (e) {
            console.log(e);
        }
    }

    /**
     * Detaches a created poster/canvas from its artwork so the worker builds a
     * fresh one. The product itself is kept - it may be published and carry
     * orders - so this only breaks the link.
     */
    async detachProduct(batch: IArtworkGenerationBatch, artwork: IArtworkToGenerate, type: 'poster' | 'canvas') {
        let label = type == 'canvas' ? 'canvasen' : 'postern';

        if (!confirm(`Koppla loss ${label} från "${artwork.name}"? En ny skapas vid nästa körning. Den befintliga produkten tas inte bort.`))
            return;

        this.detaching[`${artwork.identifier}_${type}`] = true;

        try {
            this.replaceBatch(await this.api.store().delete<IArtworkGenerationBatch>(
                `/artwork-generations/${batch._id}/artworks/${artwork.identifier}/products/${type}`));
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte koppla loss produkten';
        }
        finally {
            delete this.detaching[`${artwork.identifier}_${type}`];
        }
    }

    isDetaching(artwork: IArtworkToGenerate, type: 'poster' | 'canvas') {
        return !!this.detaching[`${artwork.identifier}_${type}`];
    }

    async setProductCreation(batch: IArtworkGenerationBatch, field: 'createPosters' | 'createCanvas', value: boolean) {
        try {
            let payload: any = {};
            payload[field] = value;

            // The server re-normalises Shopify publishing against the toggles,
            // so send the current options along to keep them.
            if (batch.shopify)
                payload.shopify = batch.shopify;

            this.replaceBatch(await this.api.store().patch<IArtworkGenerationBatch>(`/artwork-generations/${batch._id}`, payload));
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte spara inställningen';
        }
    }

    /**
     * Per-batch editing state for the Shopify options shown on a batch card.
     * Kept outside the batch objects so a poll refresh cannot wipe an in-progress
     * edit, and seeded with the same defaults as the new-batch form when the
     * batch has no sizes stored yet.
     */
    private seedShopifyEditor(batch: IArtworkGenerationBatch | null) {
        let key = this.categoryKey(batch);

        if (this.shopifyEditors[key])
            return;

        this.seedCategoryState(batch);
        this.shopifyEditors[key] = this.emptyShopifyEditor(batch ? batch.shopify : null);
    }

    /**
     * The Shopify options being edited in one place. `null` is the new-batch
     * form, anything else a batch card - the same key the category picker uses.
     */
    shopifyEditor(batch: IArtworkGenerationBatch | null) {
        return this.shopifyEditors[this.categoryKey(batch)];
    }

    /** True once publishing is enabled on the batch, so the options are shown. */
    shopifyEnabled(batch: IArtworkGenerationBatch | null) {
        if (!batch)
            return this.newBatch.createShopifyProduct;

        return !!(batch.shopify && batch.shopify.createProduct);
    }

    /**
     * Which product types the batch creates. Reads the draft form or a saved
     * batch, so the shared options template can gate its size lists either way.
     */
    createsPosters(batch: IArtworkGenerationBatch | null) {
        return batch ? !!batch.createPosters : this.newBatch.createPosters;
    }

    createsCanvas(batch: IArtworkGenerationBatch | null) {
        return batch ? !!batch.createCanvas : this.newBatch.createCanvas;
    }

    private shopifyPayload(batch: IArtworkGenerationBatch | null, createProduct: boolean) {
        let editor = this.shopifyEditor(batch);

        return {
            createProduct: createProduct,
            posterSizes: editor ? this.selectedSizes(editor.posterSizes) : [],
            canvasSizes: editor ? this.selectedSizes(editor.canvasSizes) : [],
            posterPrices: editor ? editor.posterPrices : {},
            canvasPrices: editor ? editor.canvasPrices : {},
            tags: editor ? editor.tags : '',
            posterCategory: editor ? editor.posterCategory : null,
            canvasCategory: editor ? editor.canvasCategory : null,
            vendor: editor ? editor.vendor : DEFAULT_SHOPIFY_VENDOR,
            // The server normalises these against the toggles as well, but
            // sending a finish the batch cannot have would make the saved options
            // disagree with what the form showed.
            createPassepartoutVariants: !!(editor && editor.createPassepartoutVariants)
                && createProduct && this.createsPosters(batch),
            passepartoutPrices: editor ? editor.passepartoutPrices : {},
            createMockup: !!(editor && editor.createMockup) && createProduct,
            mockupPrompt: editor ? editor.mockupPrompt : DEFAULT_MOCKUP_PROMPT,
            useCanvasMockupPrompt: !!(editor && editor.useCanvasMockupPrompt) && this.createsCanvas(batch),
            canvasMockupPrompt: editor ? editor.canvasMockupPrompt : DEFAULT_CANVAS_MOCKUP_PROMPT,
            publishProduct: !!(editor && editor.publishProduct) && createProduct
        };
    }

    /**
     * Toggles Shopify publishing on an existing batch. Enabling it also sends the
     * current size selection, so a batch created without Shopify gets usable
     * defaults rather than empty size lists the worker would skip.
     */
    async setShopifyCreation(batch: IArtworkGenerationBatch, value: boolean) {
        try {
            this.replaceBatch(await this.api.store().patch<IArtworkGenerationBatch>(`/artwork-generations/${batch._id}`, {
                shopify: this.shopifyPayload(batch, value)
            }));
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte spara Shopify-inställningen';
        }
    }

    /** Saves the size selection and tags edited on a batch card. */
    async saveShopifyOptions(batch: IArtworkGenerationBatch) {
        let editor = this.shopifyEditor(batch);

        if (!editor)
            return;

        editor.saving = true;

        try {
            this.replaceBatch(await this.api.store().patch<IArtworkGenerationBatch>(`/artwork-generations/${batch._id}`, {
                shopify: this.shopifyPayload(batch, true)
            }));
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte spara Shopify-inställningarna';
        }
        finally {
            editor.saving = false;
        }
    }

    /** Whether the Shopify toggle should be offered here. Null is the new form. */
    canOfferShopifyFor(batch: IArtworkGenerationBatch | null) {
        return this.isShopifyStore && (this.createsPosters(batch) || this.createsCanvas(batch));
    }

    /**
     * Passepartout is a poster finish published to Shopify, so it can only be
     * offered where posters are created and publishing is on.
     */
    canOfferPassepartoutFor(batch: IArtworkGenerationBatch | null) {
        return this.canOfferShopifyFor(batch)
            && this.shopifyEnabled(batch)
            && this.createsPosters(batch);
    }

    /**
     * A mockup is only ever used as a Shopify gallery image, so it needs
     * publishing switched on. Unlike passepartout it suits canvas as well, so it
     * does not require posters.
     */
    canOfferMockupFor(batch: IArtworkGenerationBatch | null) {
        return this.canOfferShopifyFor(batch) && this.shopifyEnabled(batch);
    }

    /** Puts the default scene back after the prompt has been edited. */
    resetMockupPromptFor(batch: IArtworkGenerationBatch | null) {
        let editor = this.shopifyEditor(batch);

        if (editor)
            editor.mockupPrompt = DEFAULT_MOCKUP_PROMPT;
    }

    /** Puts the default canvas scene back after it has been edited. */
    resetCanvasMockupPromptFor(batch: IArtworkGenerationBatch | null) {
        let editor = this.shopifyEditor(batch);

        if (editor)
            editor.canvasMockupPrompt = DEFAULT_CANVAS_MOCKUP_PROMPT;
    }

    /** Selected poster sizes that actually have a passepartout width configured. */
    passepartoutSizesFor(batch: IArtworkGenerationBatch | null) {
        let editor = this.shopifyEditor(batch);

        if (!editor)
            return [];

        return this.selectedSizes(editor.posterSizes)
            .filter(size => this.passepartoutWidths[size] > 0);
    }

    shopifySummary(batch: IArtworkGenerationBatch) {
        if (!batch.shopify || !batch.shopify.createProduct)
            return '';

        let parts: string[] = [];

        if (batch.createPosters && batch.shopify.posterSizes && batch.shopify.posterSizes.length)
            parts.push(`Poster: ${batch.shopify.posterSizes.join(', ')}`);

        if (batch.createCanvas && batch.shopify.canvasSizes && batch.shopify.canvasSizes.length)
            parts.push(`Canvas: ${batch.shopify.canvasSizes.join(', ')}`);

        if (batch.shopify.createPassepartoutVariants)
            parts.push('Passepartout: ja');

        if (batch.shopify.createMockup)
            parts.push(batch.shopify.useCanvasMockupPrompt ? 'Mockup: ja (egen för canvas)' : 'Mockup: ja');

        if (batch.shopify.vendor)
            parts.push(`Leverantör: ${batch.shopify.vendor}`);

        parts.push(batch.shopify.publishProduct ? 'Publiceras: ja' : 'Publiceras: nej');

        if (batch.shopify.tags && batch.shopify.tags.length)
            parts.push(`Taggar: ${batch.shopify.tags.join(', ')}`);

        return parts.join(' · ');
    }

    async retryFailed(batch: IArtworkGenerationBatch) {
        try {
            this.replaceBatch(await this.api.store().post<IArtworkGenerationBatch>(`/artwork-generations/${batch._id}/retry-failed`, {}));
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte köa om de misslyckade bilderna';
        }
    }

    async deleteDraft(batch: IArtworkGenerationBatch) {
        if (!confirm('Ta bort utkastet?'))
            return;

        try {
            await this.api.store().delete(`/artwork-generations/${batch._id}`);
            await this.load();
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte ta bort utkastet';
        }
    }


    // -------------------------------------------------------------------------
    // View helpers
    // -------------------------------------------------------------------------

    toggleExpanded(batch: IArtworkGenerationBatch) {
        let id = String(batch._id);
        this.expanded[id] = !this.expanded[id];
    }

    isExpanded(batch: IArtworkGenerationBatch) {
        return !!this.expanded[String(batch._id)];
    }

    togglePrompt(artwork: IArtworkToGenerate) {
        let shown = !this.promptShown[artwork.identifier];
        this.promptShown[artwork.identifier] = shown;

        // A rejected artwork's prompt is editable, so open it with the stored
        // text rather than whatever was left from a previous edit.
        if (shown && artwork.status == 'rejected')
            this.promptDrafts[artwork.identifier] = artwork.fullPrompt;
    }

    /**
     * An artwork that was re-queued still shows its previous image until the new
     * one lands, so the tile marks it as being replaced.
     */
    isRegenerating(artwork: IArtworkToGenerate) {
        return !!artwork.generatedArtwork
            && (artwork.status == 'pending' || artwork.status == 'generating');
    }

    /** The prompt is only editable while the artwork is rejected and awaiting a retry. */
    canEditPrompt(artwork: IArtworkToGenerate) {
        return artwork.status == 'rejected';
    }

    async savePrompt(batch: IArtworkGenerationBatch, artwork: IArtworkToGenerate) {
        let draft = (this.promptDrafts[artwork.identifier] || '').trim();

        if (!draft)
            return;

        this.savingPrompt[artwork.identifier] = true;

        try {
            this.replaceBatch(await this.api.store().patch<IArtworkGenerationBatch>(
                `/artwork-generations/${batch._id}/artworks/${artwork.identifier}`,
                { fullPrompt: draft }));

            this.promptShown[artwork.identifier] = false;
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte spara prompten';
        }
        finally {
            this.savingPrompt[artwork.identifier] = false;
        }
    }

    /** Toggles regeneration of rejected artworks for a batch. */
    async setRegenerateRejected(batch: IArtworkGenerationBatch, value: boolean) {
        try {
            this.replaceBatch(await this.api.store().patch<IArtworkGenerationBatch>(
                `/artwork-generations/${batch._id}`, { regenerateRejected: value }));
        }
        catch (e) {
            console.log(e);
            this.error = 'Kunde inte spara inställningen';
        }
    }

    /** Opens a generated artwork full size. */
    openArtwork(artwork: IArtworkToGenerate) {
        if (artwork.generatedArtwork)
            this.previewedArtwork = artwork;
    }

    closeArtwork() {
        this.previewedArtwork = null;
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        this.closeArtwork();
    }

    countByStatus(batch: IArtworkGenerationBatch, status: string) {
        return batch.artworks.filter(a => a.status == status).length;
    }

    generatedCount(batch: IArtworkGenerationBatch) {
        return batch.artworks.filter(a => !!a.generatedArtwork).length;
    }

    isDraft(batch: IArtworkGenerationBatch) {
        return batch.status == 'draft';
    }

    isLive(batch: IArtworkGenerationBatch) {
        return LIVE_STATUSES.indexOf(batch.status) >= 0;
    }

    /** Product toggles only make sense once there is artwork to turn into products. */
    canConfigureProducts(batch: IArtworkGenerationBatch) {
        return batch.status == 'completed' || batch.status == 'creating_posters';
    }

    statusLabel(status: string) {
        switch (status) {
            case 'draft': return 'Utkast';
            case 'pending': return 'Köad';
            case 'processing': return 'Genererar';
            case 'completed': return 'Klar';
            case 'creating_posters': return 'Skapar produkter';
            case 'failed': return 'Misslyckad';
            case 'generating': return 'Genererar';
            case 'approved': return 'Godkänd';
            case 'rejected': return 'Nekad';
            default: return status;
        }
    }

    modeLabel(mode: string) {
        return mode == 'synchronous' ? 'Direkt' : 'Batch';
    }

    modelLabel(model: ArtworkGenerationModel) {
        let definition = ARTWORK_GENERATION_MODELS[model];
        return definition ? definition.label : model;
    }

    qualityLabel(quality: ArtworkGenerationQuality) {
        switch (quality) {
            case 'low': return 'Låg';
            case 'medium': return 'Mellan';
            case 'high': return 'Hög';
            default: return 'Auto';
        }
    }

    rendersAtFixedSize(model: ArtworkGenerationModel) {
        let definition = ARTWORK_GENERATION_MODELS[model];
        return !!(definition && definition.sizes);
    }

    /** Each model has its own sensible quality, e.g. medium for gpt-image-1-mini. */
    onModelChange(model: ArtworkGenerationModel) {
        let definition = ARTWORK_GENERATION_MODELS[model];

        if (definition)
            this.newBatch.quality = definition.defaultQuality;
    }

    posterUrl(posterId: string) {
        return this.globals.storeUrl(`/posters/${posterId}`);
    }
}
